import axios from 'axios';

import { authEnabled } from '../auth/config';

import { invokeLogout } from './logoutRegistry';
import { getCurrentEnvForApi } from './apiBaseUrl';
import { getHomeEnv } from './runtimeHomeEnv';
import { getTenantHome } from './runtimeTenantHome';

// Re-export setRuntimeHomeEnv so tests/callers can import it from here too,
// without creating a circular dep (useAppEnv imports from runtimeHomeEnv directly
// to avoid the axiosClient mock shadowing it in tests).
export { setRuntimeHomeEnv } from './runtimeHomeEnv';

/**
 * POST-read allowlist: these are reads implemented as POST (body carries params,
 * not a write intent). They are safe to proxy to a remote env for cross-env diff
 * / export views. Export a predicate so G5's UI can mirror the contract.
 */
export const POST_READ_PROXY_ALLOWLIST: readonly string[] = [
  '/api/export/version-diff',
  '/api/export/version-zip',
  '/api/export/all-zip/async',
  '/api/export/version-zip/async',
];

/**
 * Sync-from-Platform APPLY routes to the SELECTED env like reads: it onboards
 * that env's platform tenant into that env's config, so the write belongs on
 * the selected env by definition. The TARGET env's own fence is the real gate
 * (homeable envs accept the onboarding write; prod rejects it server-side).
 */
export const isSyncApplyEndpoint = (url: string): boolean =>
  url.startsWith('/api/sync-from-platform/') && url.endsWith('/apply');

export const isPostReadEndpoint = (url: string): boolean =>
  POST_READ_PROXY_ALLOWLIST.some((p) => url === p || url.startsWith(p + '/')) ||
  // Sync-from-Platform PREVIEW is a dry-run (BEGIN → import → ROLLBACK): a read
  // implemented as POST. It must browser-direct to the SELECTED env like other
  // reads — otherwise previewing while viewing a remote env dies client-side
  // with a synthetic ENV_READ_ONLY before the request is even sent. Mirrors the
  // server fence exemption in server/src/index.ts. (The sibling /apply also
  // routes to the selected env — see isSyncApplyEndpoint above.)
  (url.startsWith('/api/sync-from-platform/') && url.endsWith('/preview'));

/**
 * Platform DATA-op write allowlist: consumer/member data (NOT config.*). When
 * viewing a remote env you may legitimately reset a consumer's tasks, edit card
 * status, assign cohorts, or import members FOR THAT env — those writes must
 * route to the SELECTED env (browser-direct, like reads), not be rejected by the
 * read-only belt. The write itself is gated server-side: the target's fence
 * bypasses these prefixes (DATA_OP_WRITE_PREFIXES) and authenticates the real
 * user via the shared-audience bearer. Mirrors the backend list.
 */
export const DATA_OP_WRITE_PREFIXES: readonly string[] = [
  '/api/consumer360',
  '/api/users',
  '/api/member-import',
  // Platform org entities (tenant.customer / tenant.sponsor) — additive INSERTs,
  // no config.* staging layer, so they route to the SELECTED env like other
  // data ops. Mirrors the backend list in server/src/index.ts.
  '/api/customers',
  '/api/sponsors',
  // Admin-view RBAC / portal-user management — env-local operational data, not
  // the config.* catalog. Mirrors the backend list. (Globals editing endpoints
  // under /api/admin/globals|catalogs|... are intentionally excluded.)
  '/api/admin/users',
  // RBAC v2 administration (CPT-1132/1133/1134/1135) — permission catalog
  // reads, role personas, single-role assignment, tenant groups. Same class
  // as /api/admin/users above. Supersedes the dead /api/admin/rbac prefix
  // (decisions §17 — the legacy RBAC console + its routes are removed).
  '/api/admin/rbac-v2',
  '/api/admin/access-grants',
];

export const isDataOpWriteEndpoint = (url: string): boolean =>
  DATA_OP_WRITE_PREFIXES.some((p) => url === p || url.startsWith(p + '/'));

// In production, nginx proxies /api/* to the backend, so no baseURL needed.
// In local dev, falls back to http://localhost:4000.
const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:4000';

const axiosClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Guard against multiple 401s triggering logout in parallel (e.g. React Query
// firing 5 queries that all 401 at once). The first one wins; the rest are
// no-ops because Auth0's logout already has the browser on its way out.
let logoutInFlight = false;

// On 401 when auth is configured: stash the current URL as the post-login
// returnTo, then force a clean re-auth by logging OUT of Auth0 first (so any
// stale/mismatched session is cleared), then relying on Auth0's post-logout
// redirect to bring the user back to `/`. ProtectedRoute will then notice
// `!isAuthenticated` and trigger loginWithRedirect; onRedirectCallback
// restores the deep link via the stashed returnTo.
axiosClient.interceptors.response.use(
  (response) => {
    const ce = (response.config as { __crossEnv?: string } | undefined)?.__crossEnv;
    if (ce) emitEnvReach(ce, true);
    return response;
  },
  (error) => {
    const ce = (error.config as { __crossEnv?: string } | undefined)?.__crossEnv;
    // Only emit unreachable for genuine transport/upstream failures:
    //   - No response at all (network timeout, connection refused)
    //   - 502 with code ENV_PROXY_UPSTREAM (the env-proxy itself is saying
    //     the target is down)
    // A business error (4xx, 5xx with a normal body) must NOT mark the env
    // unreachable — a 404 for an unpromoted tenant or a 403 permission error
    // is a valid application response, not an infrastructure failure.
    if (ce) {
      const isTransportFailure =
        !error.response ||
        (error.response.status === 502 &&
          (error.response.data as { code?: string } | undefined)?.code === 'ENV_PROXY_UPSTREAM');
      if (isTransportFailure) emitEnvReach(ce, false);
    }
    if (error.response?.status === 401 && authEnabled && !logoutInFlight) {
      // A 401 from a CROSS-ENV (remote) read must NOT tear down the HOME session
      // — it means the remote env rejected our token (e.g. no grant there), not
      // that the home session expired. Surface it as a remote-unreachable signal
      // instead of logging out (which would also re-introduce a logout loop).
      if (ce) {
        emitEnvReach(ce, false);
        return Promise.reject(error);
      }
      logoutInFlight = true;
      try {
        const here = window.location.pathname + window.location.search + window.location.hash;
        if (here && here !== '/' && here !== '/access-denied') {
          window.sessionStorage.setItem('postLoginReturnTo', here);
        }
      } catch {
        /* ignore storage failure */
      }

      // Prefer Auth0 logout: hard-clears the Auth0 session cookie so the next
      // login is a fresh round-trip (no silent-auth reusing a stale token).
      // If no handler is registered (pre-Auth0-Provider boot race), fall back
      // to a hard redirect to `/`.
      const handled = invokeLogout();
      if (!handled) {
        window.location.replace('/');
      }
    }
    return Promise.reject(error);
  },
);

// Map a ladder env token (dev01|qa|uat|integ|prod) to its PUBLIC host. Mirrors
// the backend deployedEnv() collapse (dev01 -> dev; everything else identity).
const ENV_HOST: Record<string, string> = {
  dev01: 'dev',
  newdev: 'dev',
  development: 'dev',
  integration: 'integ',
};
// Exported so the multi-env hooks (useTenantVersions / useEnvVersions) can also
// go browser-direct to a remote env's public host instead of proxying through
// the home pod's /api/env-proxy (which the service mesh blocks).
export const remoteEnvBaseUrl = (env: string): string =>
  // prod's public host has NO env segment — it's dosa.sunnyrewards.com, unlike
  // dosa.qa/uat/integ.sunnyrewards.com — so it can't come from the template below.
  env === 'prod'
    ? 'https://dosa.sunnyrewards.com'
    : `https://dosa.${ENV_HOST[env] ?? env}.sunnyrewards.com`;

// Reverse of ENV_HOST: a public-host segment → its canonical ladder env token.
const HOST_TO_ENV: Record<string, string> = { dev: 'dev01', integ: 'integ' };

// The env whose PORTAL the app is currently served from, derived from the
// browser host (dosa.uat.sunnyrewards.com → uat; dosa.dev… → dev01;
// dosa.sunnyrewards.com → prod). This is the "same-origin" backend — distinct
// from the tenant's HOME env and from the SELECTED (dropdown) env. Falls back to
// the build-time editable env for local dev / unknown hosts.
export function getPortalEnv(): string {
  const host =
    (typeof window !== 'undefined' && window.location?.hostname) || '';
  if (host === 'dosa.sunnyrewards.com') return 'prod';
  const m = host.match(/^dosa\.([a-z0-9]+)\.sunnyrewards\.com$/i);
  if (m) {
    const seg = m[1].toLowerCase();
    return HOST_TO_ENV[seg] ?? seg;
  }
  return process.env.REACT_APP_EDITABLE_ENV ?? 'dev01';
}

// Endpoints whose data is TENANT-HOME-authoritative: promotion workflows,
// version pointers, eligibility, and the global tenant-home map all live on the
// tenant's home env. They must answer from the HOME env regardless of which
// portal the operator logged into (else the Promote page on dosa.uat queries
// UAT's empty promotion tables for a dev01-homed tenant). Distinct from the
// viewer/deployment-local endpoints (/api/app-env, /api/me, /api/rbac/my-*)
// which always answer from THIS portal.
function isHomeAuthoritative(url: string): boolean {
  return (
    url.startsWith('/api/promotion-workflow') ||
    url.startsWith('/api/promotion-version') ||
    url.startsWith('/api/promotion-eligibility') ||
    url.startsWith('/api/admin/editing-env-map')
  );
}

// Extract the tenant code from a request URL — either the `?tenantCode=` query
// param or a `/tenant(s)/<code>/` path segment. Used to look up that tenant's
// authoring home so home-authoritative reads route there.
function tenantCodeFromUrl(url: string): string | undefined {
  const q = url.match(/[?&]tenantCode=([^&]+)/);
  if (q) return decodeURIComponent(q[1]);
  const p = url.match(/\/tenants?\/([^/?]+)/);
  return p ? decodeURIComponent(p[1]) : undefined;
}

// Cross-env read-only view. The browser authors against its OWN backend; to VIEW
// a remote env it calls that env's PUBLIC host DIRECTLY with our Auth0 token. All
// envs share one Auth0 tenant/audience, so the token validates on the target, and
// each env's CORS allows the sibling dosa.* origin. (This previously proxied
// through the home backend's signed read-gateway, but the home pods can't reach
// sibling envs over the service mesh — the browser can, over the public host.)
// Registered after the auth request interceptor, so the bearer is already
// attached. Exported (not inlined) so the routing matrix is unit-testable.
export function crossEnvRequestRouter(config: any): any {
  const url = config.url ?? '';
  const method = (config.method ?? 'get').toLowerCase();
  const homeEnv = getHomeEnv();

  // (A) TENANT-HOME-AUTHORITATIVE reads → always answer from the TENANT's HOME
  //     env, independent of the dropdown AND of which portal you're on. The home
  //     that matters is PER-TENANT (from useTenantEditability, e.g. dev01), NOT
  //     the deployment's global home (getHomeEnv() is `uat` on the uat portal).
  //     We look the tenant's home up by the request's tenantCode and route
  //     browser-direct to dosa.<home> when it differs from the portal we're on.
  //     Falls back to the global home for tenant-less endpoints (editing-env-map)
  //     or before editability has resolved. GET/HEAD only.
  //
  //     EXCEPTION: a `__envProbe`-pinned request deliberately targets ONE env's
  //     own pointer (the deploy matrix's per-env sweep via useTenantVersions,
  //     which probes dev01 AND qa AND uat… independently). Those must NOT be
  //     folded back to the tenant's home — otherwise the LOCAL leg (the env the
  //     portal serves, sent as a relative /api/ URL) gets rewritten to dosa.<home>
  //     and the matrix shows the home env's version in every column you view from.
  const isEnvProbe = !!(config as { __envProbe?: string }).__envProbe;
  if (
    (method === 'get' || method === 'head') &&
    url.startsWith('/api/') &&
    isHomeAuthoritative(url) &&
    !isEnvProbe
  ) {
    const home = getTenantHome(tenantCodeFromUrl(url)) ?? homeEnv;
    if (home && home !== getPortalEnv()) {
      config.url = `${remoteEnvBaseUrl(home)}${url}`;
      (config as { __crossEnv?: string }).__crossEnv = home;
      return config;
    }
  }

  // (B) Cross-env VIEW of a SELECTED remote env.
  const selected = getCurrentEnvForApi();
  // "Same-origin" is the env whose backend actually SERVED this portal, derived
  // from the HOSTNAME (dosa.qa → qa) — NOT getHomeEnv(). With dev01 now the
  // single editable env on EVERY domain, getHomeEnv() resolves to dev01 even on
  // the qa portal; comparing `selected` against it treated dev01-selected as
  // "home" and left the request same-origin, so it hit the QA backend (the
  // banner said "connected to qa" with DEV01 in the dropdown, and you saw qa's
  // data). Compare against the portal's REAL env — matching block (A) above — so
  // any selection that differs from the serving backend routes browser-direct.
  const portalEnv = getPortalEnv();
  if (!selected || selected === portalEnv) return config; // already this backend

  if (method !== 'get' && method !== 'head') {
    // Portal = viewport: mutations route browser-direct to the SELECTED env,
    // whose own write fence (env Layer 1 + per-tenant Layer 2) is the
    // authoritative gate — a wrong-home write 403s there with a precise code.
    // The client-side belt blocks only PROD, and only for writes prod's own
    // fence would reject anyway, so we fail fast without a round-trip.
    //
    // EXCEPTION 1: /api/customer-defaults — per-customer auth_config + secret
    // defaults are ENV-LOCAL (each env has its own Auth0 wiring/credentials)
    // and a super-admin must be able to configure them on EVERY env, prod
    // included. The target env's server enforces requireRole('super-admin');
    // its read-only fence exempts this path by design.
    //
    // EXCEPTION 2: POST-shaped READS (isPostReadEndpoint) — Sync-from-Platform
    // /preview (BEGIN → import → ROLLBACK, writes nothing) and the async export
    // kick endpoints. These are reads that must work on ANY selected env,
    // prod included; the prod server fence already exempts them. Without this,
    // "Sync from Platform → Preview import" on the prod view dies here with a
    // synthetic ENV_READ_ONLY before the request is even sent. (The sibling
    // /apply is NOT a read — it stays blocked on prod.)
    // EXCEPTION 3: Sync-from-Platform /apply (isSyncApplyEndpoint) — an
    // ONBOARDING write that legitimately targets the selected env. Bringing a
    // prod-native platform tenant into config can ONLY be done on prod. The
    // server is the authoritative gate (index.ts exempts /apply from the
    // Layer-1 read-only fence and defers to requirePerTenantEditable, which
    // allows onboarding a not-yet-managed tenant), so this belt must not
    // fail-fast it client-side — let it browser-direct like the others.
    //
    // EXCEPTION 4: platform DATA ops (isDataOpWriteEndpoint) — consumer/member
    // data, org entities and portal-access administration. These are env-local
    // and NOT part of the config.* promotion model, so prod's own fence lets
    // them through unconditionally (server/src/index.ts: `if (isDataOpWrite
    // (req.path)) return next();` — no prod check, deliberately unlike the
    // globals-authoring bypass right below it, which does exclude prod). This
    // belt was rejecting them here first, so "attach cohort" on the prod view
    // died with a synthetic ENV_READ_ONLY and a generic "Failed to attach
    // cohort" toast before any request was sent. DATA_OP_WRITE_PREFIXES and
    // isDataOpWriteEndpoint were declared for exactly this and had no caller —
    // the carve-out was lost when this belt was narrowed to prod-only (the
    // original fix was PR #233, when the belt rejected on EVERY remote env).
    const reqUrl = config.url ?? '';
    if (selected === 'prod'
        && !reqUrl.startsWith('/api/customer-defaults')
        && !isPostReadEndpoint(reqUrl)
        && !isSyncApplyEndpoint(reqUrl)
        && !isDataOpWriteEndpoint(reqUrl)) {
      return Promise.reject(
        Object.assign(new Error('ENV_READ_ONLY'), {
          config,
          response: {
            status: 403,
            data: {
              code: 'ENV_READ_ONLY',
              // Callers surface `data.error` ("...|| 'Failed to attach cohort'"),
              // so without this every blocked write showed its own generic
              // fallback and the reason only existed in the console.
              error: 'PROD is read-only in the portal — this change must be made on the '
                   + 'editable env and promoted. (Consumer and member data ops are allowed '
                   + 'on prod; config authoring is not.)',
            },
          },
          isAxiosError: true,
        }),
      );
    }
    // Everything else falls through to the browser-direct rewrite below
    // (same as GET; home-only endpoints keep answering from THIS deployment).
  }

  // Phase 7 — full cross-env read-only view: when viewing a remote env, proxy
  // ALL /api GET reads to that env's signed read-gateway, so the whole UI shows
  // the selected env's data (an unpromoted tenant on qa comes back empty).
  // EXCLUDE home-only endpoints that must always answer from THIS deployment:
  //   - /api/app-env               : this pod's manifest/editability (never the target's)
  //   - /api/me / /api/me/*        : the home Auth0 identity (the viewer, not a target user)
  //   - /api/env-proxy/*           : already proxied — don't double-wrap
  //   - /api/promotion-workflow/*  : promotion state is home-authoritative
  //   - /api/promotion-version/*   : version pointers managed on the home env
  //   - /api/promotion-eligibility : eligibility computed relative to the home
  //   - /api/admin/editing-env-map : home's global tenant-home map
  //   - /api/rbac/my-*             : current-user-scoped reads resolve via the
  //                                  injected service user on the target, which
  //                                  is 'env-proxy-read@internal' — NOT the real
  //                                  viewer's email. My-permissions/my-roles must
  //                                  always come from the home Auth0 surface.
  //   - /api/tenant/*/my-access    : viewer's access grants live in the PORTAL's
  //                                  own database, not the selected env's. Routing
  //                                  this cross-env meant QA-portal users were
  //                                  checked against dev01's app_user / grant
  //                                  tables (a different DB), where their row may
  //                                  not exist or may carry different grants —
  //                                  causing a false "read-only" on initial load.
  const isHomeOnly =
    url === '/api/app-env' ||
    url === '/api/me' ||
    url.startsWith('/api/me/') ||
    url.startsWith('/api/env-proxy/') ||
    url.startsWith('/api/promotion-workflow') ||
    url.startsWith('/api/promotion-version') ||
    url.startsWith('/api/promotion-eligibility') ||
    url.startsWith('/api/admin/editing-env-map') ||
    url.startsWith('/api/rbac/my-') ||
    url.startsWith('/api/admin/rbac/my-') ||
    url.endsWith('/my-access');
  if (url.startsWith('/api/') && !isHomeOnly) {
    // Browser-direct: rewrite to an ABSOLUTE URL on the remote env's public host.
    // axios uses an absolute url as-is (baseURL ignored); the shared-audience
    // bearer is already attached and the target's CORS allows this origin.
    config.url = `${remoteEnvBaseUrl(selected)}${url}`;
    // Tag so the response interceptor can emit a reachability signal for the
    // graceful "Couldn't reach {env}" banner (vs a generic per-page error),
    // and so the 401 handler skips home-logout for a remote auth failure.
    (config as { __crossEnv?: string }).__crossEnv = selected;
  }
  return config;
}
axiosClient.interceptors.request.use(crossEnvRequestRouter);

/**
 * Emit a coarse reachability signal for the currently-viewed REMOTE env so a
 * single banner can say "Couldn't reach {env}" instead of every page showing a
 * generic "failed to load". Cross-env reads all go through the env-proxy, so
 * they fail together (502 ENV_PROXY_UPSTREAM) when the target is down and
 * succeed together when it's up — a last-event-wins signal is stable enough.
 */
function emitEnvReach(env: string | undefined, reachable: boolean): void {
  if (!env || typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent('env-proxy-status', { detail: { env, reachable } }));
  } catch {
    /* no-op */
  }
}

export default axiosClient;
